import { DateFormatType, formatDate } from "~/utils/datetime"
import {
  effectiveFramingAnswer,
  isFramingOption,
  parseFramingAnswer,
  type ParsedFramingAnswer,
} from "~/utils/demandFraming"

/**
 * How the `seasonTrend` answer applies its measured rate; shared by the engine and Settings row.
 * Every mode is TOP-DOWN on the comparable season's sales (`year1 × rate`), split across the
 * deepest nodes at each node's reconciled rate, then across a node's variants by baseline share.
 * Never on the plan's baseline: built from matches, it can be several times the comparable's sales.
 *
 *  - `pct`: the measured rate; on a node its reconciled rate, so nodes sum to the level exactly.
 *  - `pctRaw` (node only): the node's own unscaled rate; moves the total by its own difference.
 *  - `units`: the units the comparable grew by (`year1 − year2`), per node.
 *  - `custom`: the planner's own rate on the comparable's sales, per node.
 *  - `customUnits`: the planner's signed units, split by share of the comparable's sales
 *    (`levelYear1`); a node answering it on its own adds exactly the figure.
 *  - `none`: the DEFAULT, also while unanswered: the rate is shown but every variant keeps its
 *    baseline. Distinct from switching the insight off: a node can answer `none` under a
 *    collection that applies the rate.
 */
export type SeasonTrendMode = {
  optionId: "pct" | "pctRaw" | "units" | "custom" | "customUnits" | "none"
  customPct: number | null
  /** Set on `customUnits` only. */
  customUnits?: number | null
}

// Must agree with `FRAMING_QUESTIONS.seasonTrend.defaultOptionId`, or the plan sizes differently
// from the radio.
export const DEFAULT_SEASON_TREND_MODE: SeasonTrendMode = { optionId: "none", customPct: null }

const isModeOption = (id: string): id is SeasonTrendMode["optionId"] =>
  id === "pct" ||
  id === "pctRaw" ||
  id === "units" ||
  id === "custom" ||
  id === "customUnits" ||
  id === "none"

/** Two answers that size the same: the option and, where it carries one, the planner's figure. */
export const sameSeasonTrendMode = (a: SeasonTrendMode, b: SeasonTrendMode): boolean =>
  a.optionId === b.optionId &&
  (a.customPct ?? null) === (b.customPct ?? null) &&
  (a.customUnits ?? null) === (b.customUnits ?? null)

/** Keyed like `DemandFramingAnswer`; `""` / `""` = the whole collection. */
export type SeasonTrendNodeMode = { groupKey: string; groupValue: string; mode: SeasonTrendMode }

/**
 * The collection's answer, then each level's node in hierarchy order, deeper overriding wider: the
 * same walk as `effectiveKeysForVariant`. A blank value (uncategorised) holds no answer.
 */
export const resolveSeasonTrendMode = (
  whole: SeasonTrendMode,
  nodes: readonly SeasonTrendNodeMode[],
  levels: readonly string[],
  nodeValueOf: (levelKey: string) => string | null | undefined,
): SeasonTrendMode => {
  let mode = whole
  if (nodes.length === 0) return mode
  for (const groupKey of levels) {
    const groupValue = nodeValueOf(groupKey)
    if (!groupValue) continue
    const own = nodes.find((n) => n.groupKey === groupKey && n.groupValue === groupValue)
    if (own) mode = own.mode
  }
  return mode
}

/** A stored `seasonTrend` answer as a mode; anything unknown or malformed reads as the default. */
export const parseSeasonTrendMode = (answer: string | null | undefined): SeasonTrendMode => {
  const effective = effectiveFramingAnswer("seasonTrend", answer)
  if (effective == null || !isFramingOption("seasonTrend", effective))
    return DEFAULT_SEASON_TREND_MODE
  const parsed = parseFramingAnswer(effective) as ParsedFramingAnswer
  if (!isModeOption(parsed.optionId)) return DEFAULT_SEASON_TREND_MODE
  // The stored number rides in `customPct` whatever the option; only `customUnits` reads it as units.
  if (parsed.optionId === "customUnits")
    return { optionId: parsed.optionId, customPct: null, customUnits: parsed.customPct }
  return { optionId: parsed.optionId, customPct: parsed.customPct }
}

/**
 * A measured slice: the level or one node. `appliedPct` is the rate the level's reconciliation
 * hands a node (`NodeSalesTrend.appliedPct`); absent on the level, whose rate IS the target.
 */
export type MeasuredSeasonTrend = {
  pct: number | null
  year1: number
  year2: number
  appliedPct?: number | null
  /**
   * The LEVEL's comparable sales when this slice is a node following the collection's answer: what
   * `customUnits` shares out by. Absent on the level and on a node answering on its own.
   */
  levelYear1?: number
}

/**
 * The growth is the whole level's, so a route covering only part of it has nothing that adds up to
 * spread it over. A collection-grain lineage always qualifies.
 */
export const seasonTrendApplicable = (
  lineageGrain: "collection" | "group",
  levelCollectionIds: readonly number[],
  routeCollectionIds: readonly number[],
): boolean =>
  lineageGrain === "collection" || levelCollectionIds.every((id) => routeCollectionIds.includes(id))

/**
 * Units a mode adds on a measured slice: COMPARABLE-SEASON SALES × rate, never the plan's baseline.
 * `pct` on a node uses the reconciled rate, so nodes sum to the level's `year1 × pct` exactly. Null
 * with no rate. `customUnits`: a following node takes its `levelYear1` share of the figure.
 */
export const modeUnits = (mode: SeasonTrendMode, measured: MeasuredSeasonTrend): number | null => {
  if (measured.pct == null) return null
  switch (mode.optionId) {
    case "none":
      return 0
    case "custom":
      return (measured.year1 * (mode.customPct ?? 0)) / 100
    case "customUnits": {
      const units = mode.customUnits ?? 0
      return measured.levelYear1 != null && measured.levelYear1 > 0
        ? (units * measured.year1) / measured.levelYear1
        : units
    }
    case "units":
      return measured.year1 - measured.year2
    case "pctRaw":
      return (measured.year1 * measured.pct) / 100
    case "pct":
      return (measured.year1 * (measured.appliedPct ?? measured.pct)) / 100
  }
}

/** Sized by the measurement; the ceiling covers them so switching cannot move the axis. */
const DETERMINISTIC_MODES: readonly SeasonTrendMode[] = [
  { optionId: "none", customPct: null },
  { optionId: "units", customPct: null },
  { optionId: "pctRaw", customPct: null },
  { optionId: "pct", customPct: null },
]

/**
 * The chart's pinned ceiling. `custom` and `customUnits` are unbounded, so they count at their
 * current value: a ceiling for an arbitrary number would flatten the real columns.
 */
export const seasonTrendCeiling = (
  measured: MeasuredSeasonTrend,
  current: SeasonTrendMode,
): number =>
  Math.max(
    measured.year1,
    measured.year2,
    ...[...DETERMINISTIC_MODES, current].map(
      (mode) => measured.year1 + (modeUnits(mode, measured) ?? 0),
    ),
  )

/** A subset of `NodeSalesTrend`, as `computeLevelSalesTrend` states it. */
export type SeasonTrendNode = {
  levelKey: string
  path: string[]
  year1: number
  year2: number
  pct: number | null
  carriedPct: number | null
  appliedPct: number | null
}

/** Node identity in the maps below; a space-joined key would collide on names containing spaces. */
export const seasonTrendPathKey = (path: readonly string[]): string => path.join("\u0000")

/**
 * Step one of the top-down split: units per DEEPEST node. A node with its own answer replaces its
 * amount (and may move the total). Following nodes are scaled by one factor so that, when all
 * follow, the total IS `modeUnits` of the whole (a no-op for `units`/`custom`; within rounding for
 * `pct` unless `correctionRatio` clamped). With no levels the level is the one node. Pure, so the
 * client draws the engine's split without a second read.
 */
export const allocateSeasonTrendNodes = ({
  measured,
  nodes,
  levels,
  modes,
}: {
  measured: MeasuredSeasonTrend
  nodes: readonly SeasonTrendNode[]
  /** In hierarchy order. */
  levels: readonly string[]
  modes: { whole: SeasonTrendMode; nodes: readonly SeasonTrendNodeMode[] }
}): {
  levelUnits: number | null
  byNode: Map<string, number>
  /** Nodes following the collection's answer; they carry what other nodes cannot land. */
  following: Set<string>
} => {
  const byNode = new Map<string, number>()
  const following = new Set<string>()
  if (measured.pct == null) return { levelUnits: null, byNode, following }
  if (levels.length === 0) {
    const units = modeUnits(modes.whole, measured) ?? 0
    const key = seasonTrendPathKey([])
    byNode.set(key, units)
    following.add(key)
    return { levelUnits: units, byNode, following }
  }
  const sameMode = sameSeasonTrendMode
  // The factor is measured as if EVERY node followed, so an override neither inflates nor starves
  // its neighbours: it moves the total by its own difference.
  const deepest = nodes.filter((node) => node.path.length === levels.length)
  // A following node shares a units figure by `levelYear1`; an own-answer node is handed no level
  // and adds exactly what it answered.
  const asWhole = (node: SeasonTrendNode) =>
    modeUnits(modes.whole, {
      ...node,
      pct: node.pct ?? measured.pct,
      appliedPct: node.appliedPct,
      levelYear1: measured.year1,
    }) ?? 0
  const wholeSum = deepest.reduce((sum, node) => sum + asWhole(node), 0)
  const target = modeUnits(modes.whole, measured) ?? 0
  // Only scaled where a factor is meaningful: a zero or opposite-signed sum has nothing to scale onto.
  const factor = wholeSum !== 0 && Math.sign(wholeSum) === Math.sign(target) ? target / wholeSum : 1
  let levelUnits = 0
  for (const node of deepest) {
    const mode = resolveSeasonTrendMode(
      modes.whole,
      modes.nodes,
      levels,
      (level) => node.path[levels.indexOf(level)],
    )
    // A node with no rate carries the level's (`carriedPct`); the fallback keeps a malformed node
    // from poisoning the sum.
    const follows = sameMode(mode, modes.whole)
    const units = follows
      ? asWhole(node) * factor
      : (modeUnits(mode, { ...node, pct: node.pct ?? measured.pct, appliedPct: node.appliedPct }) ??
        0)
    const key = seasonTrendPathKey(node.path)
    byNode.set(key, units)
    if (follows) following.add(key)
    levelUnits += units
  }
  return { levelUnits, byNode, following }
}

/**
 * Step two: each node's units across its variants by baseline share. A node whose variants carry no
 * baseline cannot land its amount, so the `carriers` (nodes following the collection's answer)
 * absorb it by baseline share and the plan adds exactly the level figure. An own-answer node is
 * exactly what it answered. A variant in a node the scan never saw gets nothing.
 */
export const spreadSeasonTrendUnits = ({
  byNode,
  levels,
  variants,
  carriers,
}: {
  byNode: ReadonlyMap<string, number>
  levels: readonly string[]
  variants: readonly {
    variantId: string
    baseline: number
    nodeValueOf: (level: string) => string | null | undefined
  }[]
  /** Every node in `byNode` when absent. */
  carriers?: ReadonlySet<string>
}): Map<string, number> => {
  const unitsByVariant = new Map<string, number>()
  const members = new Map<string, { variantId: string; baseline: number }[]>()
  const nodeOf = new Map<string, string>()
  for (const v of variants) {
    const key = seasonTrendPathKey(levels.map((level) => v.nodeValueOf(level) ?? ""))
    nodeOf.set(v.variantId, key)
    const list = members.get(key) ?? []
    list.push({ variantId: v.variantId, baseline: Math.max(v.baseline, 0) })
    members.set(key, list)
  }
  let unlanded = 0
  for (const [key, units] of byNode) {
    const list = members.get(key)
    const total = list?.reduce((sum, v) => sum + v.baseline, 0) ?? 0
    if (list == null || total <= 0) {
      unlanded += units
      continue
    }
    for (const v of list) unitsByVariant.set(v.variantId, (units * v.baseline) / total)
  }
  if (unlanded !== 0 && unitsByVariant.size > 0) {
    const landed = variants.filter(
      (v) =>
        unitsByVariant.has(v.variantId) &&
        (carriers == null || carriers.has(nodeOf.get(v.variantId) ?? "")),
    )
    const pool = landed.reduce((sum, v) => sum + Math.max(v.baseline, 0), 0)
    if (pool > 0) {
      for (const v of landed) {
        unitsByVariant.set(
          v.variantId,
          unitsByVariant.get(v.variantId)! + (unlanded * Math.max(v.baseline, 0)) / pool,
        )
      }
    }
  }
  return unitsByVariant
}

/** Why the rate cannot be applied from a member of a group-linked season, and what would let it. */
export const seasonRouteRequirement = (levelName: string, groupName?: string | null): string =>
  `This ${levelName} is part of the ${groupName ?? "collection"} group, whose growth is measured for the whole group. Open the group to apply it.`

/** Which link of the three-season chain is missing, as `SeasonLineage.unavailable` names it. */
export type SeasonLineageGap = "noComparable" | "noPrior" | "unfinished"

/**
 * The three-season chain (plan, its baseline, that baseline's baseline) with the missing link as
 * `?`, then what is missing. Shared by the Settings row and the server's refusal.
 */
export const seasonLineageRequirement = ({
  gap,
  levelName,
  thisName,
  comparableName,
  priorName,
  running,
}: {
  gap: SeasonLineageGap
  /** "collection" or "group", for the fallback wording when the plan has no name. */
  levelName: string
  thisName?: string | null
  comparableName?: string | null
  /** The baseline's own baseline. */
  priorName?: string | null
  /** For `unfinished` (`SeasonSide.running`). */
  running?: readonly { name: string; end: string | null }[]
}): string => {
  const here = thisName || `This ${levelName}`
  const comparable = comparableName || "the comparable collection"
  switch (gap) {
    case "noComparable":
      return `${here} → ? → ? · No comparable collection linked to ${here}. Link one under Link to in collection settings.`
    case "noPrior":
      return `${here} → ${comparable} → ? · ${comparable} has no comparable collection of its own.`
    case "unfinished":
      return `${here} → ${comparableName || "?"} → ${priorName || "?"} · ${runningSentence(running ?? [])}`
  }
}

/** "AW26 (ends 30 Dec 2026) still running." — or the nameless fallback when the side was not described. */
const runningSentence = (running: readonly { name: string; end: string | null }[]): string => {
  if (running.length === 0) return "A collection in the chain is still running."
  const named = running.map(
    (r) =>
      `${r.name} (${r.end ? `ends ${formatDate(r.end, DateFormatType.FullDateShort)}` : "no end date"})`,
  )
  return `${named.join(", ")} still running.`
}
